import React, { useState, useEffect, useCallback } from 'react';
import { conversionDeliveryApi } from '@/services/api';
import { CheckCircle2, AlertTriangle, RefreshCw, Loader2, Beaker } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const inr = (v) => `₹${Math.round(Number(v || 0)).toLocaleString('en-IN')}`;
const day = (iso) => (iso ? new Date(iso).toLocaleDateString('en-IN',
  { day: 'numeric', month: 'short' }) : '—');

/**
 * Is Google actually receiving the money we earned?
 *
 * This card exists because it did not. The offline-conversion sweep stalled on
 * 22-Sep-2026 and nobody noticed for 15 days — ₹255k of Booked + Paid value never
 * reached Google Ads while Smart Bidding trained on ₹1 form fills. The backlog
 * number was available the whole time; nothing displayed it.
 */
export const ConversionDeliveryCard = () => {
  const [offline, setOffline] = useState(null);
  const [adj, setAdj] = useState(null);
  const [hidden, setHidden] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [o, a] = await Promise.all([
        conversionDeliveryApi.getOfflineStatus(),
        conversionDeliveryApi.getAdjustmentStatus(),
      ]);
      setOffline(o.data);
      setAdj(a.data);
    } catch (e) {
      if (e?.response?.status === 403) setHidden(true);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const sweep = async () => {
    setBusy(true);
    try {
      const { data } = await conversionDeliveryApi.runOfflineSweep();
      // The backend runs this detached — a big backlog of pushes to Google would
      // blow the gateway timeout — so it answers "started", not a tally.
      toast.success(data?.already_running
        ? 'A sweep is already running'
        : 'Sweep started — the tally updates shortly');
      setTimeout(load, 8000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Sweep failed');
    } finally {
      setBusy(false);
    }
  };

  if (hidden || !offline) return null;

  const b = offline.backlog || {};
  const stuck = (b.total_leads || 0) > 0;
  const value = (b.booked_value || 0) + (b.paid_value || 0);
  const dormant = !offline.enabled || !offline.configured;

  return (
    <div
      className={`rounded-xl border p-4 mb-6 text-sm ${stuck
        ? 'bg-amber-50 border-amber-300 text-amber-900'
        : 'bg-emerald-50 border-emerald-200 text-emerald-900'}`}
      data-testid="conversion-delivery-card"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-2">
          {stuck
            ? <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            : <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />}
          <div>
            <div className="font-semibold" data-testid="conversion-delivery-headline">
              {stuck
                ? `${b.total_leads} conversions worth ${inr(value)} have not reached Google`
                : 'Every earned conversion has reached Google'}
            </div>
            <div className="text-xs mt-1 opacity-90" data-testid="conversion-delivery-detail">
              {stuck
                ? `${b.booked_leads} Booked (${inr(b.booked_value)}) · ${b.paid_leads} Paid (${inr(b.paid_value)}) · oldest ${day(b.oldest)}`
                : `${offline.totals?.booked_sent || 0} Booked and ${offline.totals?.paid_sent || 0} Paid delivered so far`}
              {offline.stuck_since && ` · stuck since ${day(offline.stuck_since)}`}
            </div>
            {adj && (
              <div className="text-xs mt-1 opacity-90" data-testid="conversion-restatement-detail">
                Lead value restated on {adj.totals?.sent || 0} conversions
                {adj.totals?.restated_value ? ` (${inr(adj.totals.restated_value)} of real value)` : ''}
                {adj.totals?.errors ? ` · ${adj.totals.errors} errors` : ''}
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          {offline.validate_only && (
            <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-white/70 border border-current/20"
                  data-testid="conversion-delivery-validate-only">
              <Beaker className="w-3 h-3" /> validate-only
            </span>
          )}
          {dormant && (
            <span className="text-xs px-2 py-1 rounded-full bg-white/70 border border-current/20"
                  data-testid="conversion-delivery-dormant">
              dormant
            </span>
          )}
          <Button size="sm" variant="outline" onClick={sweep} disabled={busy}
                  data-testid="conversion-delivery-sweep-btn">
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
            <span className="ml-1">Send now</span>
          </Button>
        </div>
      </div>
    </div>
  );
};
